import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { tipoProdutoSchema, TipoProdutoFormData } from "@/schemas/tipoProdutoSchema";
import { TipoProduto } from "@/types/tipo-produto";

interface TipoProdutoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TipoProdutoFormData) => void;
  produto?: TipoProduto | null;
  mode: "create" | "edit";
}

export function TipoProdutoFormDialog({
  open,
  onOpenChange,
  onSubmit,
  produto,
  mode,
}: TipoProdutoFormDialogProps) {
  const form = useForm<TipoProdutoFormData>({
    resolver: zodResolver(tipoProdutoSchema),
    defaultValues: {
      nome: "",
      descricao: "",
      categoria: "",
      preco_base: 0,
      unidade_venda: "un",
      tamanho: "",
      peso: null,
      preco_custo: null,
      preco_venda: null,
      ativo: true,
      observacoes: "",
    },
  });

  useEffect(() => {
    if (open && produto && mode === "edit") {
      form.reset({
        nome: produto.nome,
        descricao: produto.descricao || "",
        categoria: produto.categoria || "",
        preco_base: produto.preco_base,
        unidade_venda: produto.unidade_venda,
        tamanho: produto.tamanho || "",
        peso: produto.peso || null,
        preco_custo: produto.preco_custo || null,
        preco_venda: produto.preco_venda || null,
        ativo: produto.ativo,
        observacoes: produto.observacoes || "",
      });
    } else if (open && mode === "create") {
      form.reset({
        nome: "",
        descricao: "",
        categoria: "",
        preco_base: 0,
        unidade_venda: "un",
        tamanho: "",
        peso: null,
        preco_custo: null,
        preco_venda: null,
        ativo: true,
        observacoes: "",
      });
    }
  }, [open, produto, mode, form]);

  const handleSubmit = (data: TipoProdutoFormData) => {
    onSubmit(data);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Novo Produto" : "Editar Produto"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ex: Pudim de Leite" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="categoria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ex: Pudim, Bolo, Salgado" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="descricao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descrição do produto" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="preco_base"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preço Base (R$) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unidade_venda"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unidade de Venda *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="un, kg, fatia, porção" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold">Informações de Beneficiamento</h3>

              <FormField
                control={form.control}
                name="tamanho"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tamanho</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ""} placeholder="Ex: 100g, 500ml, Grande" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="peso"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Peso (kg)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.001"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="0.450"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="preco_custo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Custo (R$)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="8.00"
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground">Custo após beneficiamento</p>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="preco_venda"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Venda (R$)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="15.00"
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground">Preço sugerido</p>
                    </FormItem>
                  )}
                />
              </div>

              {form.watch("preco_custo") && form.watch("preco_venda") && (
                <div className="p-3 bg-muted rounded-md">
                  <p className="text-sm font-medium">
                    💰 Margem de lucro: {' '}
                    {(((form.watch("preco_venda")! - form.watch("preco_custo")!) / form.watch("preco_custo")!) * 100).toFixed(2)}%
                  </p>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="observacoes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Observações adicionais" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ativo"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Produto Ativo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Produtos inativos não aparecem para seleção
                    </div>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {mode === "create" ? "Criar" : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
